import CouponApi from '@apis/coupon/CouponApi';
import queries from '@apis/queries';
import useLoading from '@hooks/ui/useLoading';
import useToast from '@hooks/ui/useToast';
import { DownloadableCouponRow } from '@models/coupon';
import { ErrorResponse } from '@models/error';
import { useFlow } from '@stackflow/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';

function useDownloadCoupon(
    coupon: DownloadableCouponRow,
) {
    const { eventId } = coupon;
    const queryClient = useQueryClient();
    const toast = useToast();
    const { t } = useTranslation('coupon');
    const { startLoading, stopLoading } = useLoading();
    const { push } = useFlow();
    const { mutate } = useMutation({
        mutationFn: CouponApi.downloadCoupon,
        onMutate() {
            startLoading();
        },
        onSuccess() {
            queryClient.invalidateQueries({ queryKey: queries.coupons._def, type: 'all' })
                .finally(() => {
                    push('CouponDownloadModal', {});
                });
        },
        onError(e: AxiosError<ErrorResponse>) {
            console.error(e);
            switch (e.response?.data?.code) {
            case 'ALREADY_ISSUED_COUPON':
                toast.error(t('download.alreadyIssued'));
                break;
            case 'COUPON_ISSUE_NOT_IN_PERIOD':
                toast.error(t('download.notInPeriod'));
                break;
            case 'COUPON_ISSUE_LIMIT_EXCEEDED':
                toast.error(t('download.limitExceeded'));
                break;
            case 'DAILY_COUPON_ISSUE_LIMIT_EXCEEDED':
                toast.error(t('download.dailyLimitExceeded'));
                break;
            default:
                toast.error(e.response?.data?.message || t('download.error'));
            }
        },
        onSettled() {
            stopLoading();
        },
    });
    return () => {
        mutate(eventId);
    };
}

export default useDownloadCoupon;
